import { ViewIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import {
  AdminPracticeData,
  adminRoles,
  colors,
  getPracticeWithAdmin,
  midwifeId as midwifeIdField,
  MIDWIVES,
  MIDWIVES_ADMIN,
  midwivesCollection,
  Practice,
  PracticeWithAdmin,
} from '@hb/shared'

import React, { useContext, useMemo, useState } from 'react'

import {
  AppContext,
  PopUpMessageContext,
  PracticeContext,
  useAppRole,
  useCollection,
  usePracticeAccess,
} from '@hb/react'
import { useDocument } from '../../backend/db/hooks/useDocument'
import { useProfile } from '../../contexts/user/ProfileContext'
import { useSelectedItem } from '../../hooks/backend/useSelectedItem'
import { useUpdateDoc } from '../../hooks/backend/useUpdateDoc'
import { StageReportModal } from './reports'
import { SimpleForm } from '../forms/FinalForm/SimpleForm'
import { LegacyMidwifeView } from './LegacyMidwifeView'
import { PracticeDataView } from './PracticeDataView'
import { ProviderAlertPopover } from './ProviderAlertPopover'

export const MidwifeInfo = ({ id }: { id: string | null }) => {
  const practices = useCollection(midwivesCollection)

  const { appName } = useContext(AppContext)
  const role = useAppRole()
  const { selectedPracticeId } = usePracticeAccess()
  const provider = useSelectedItem(practices?.items, id)
  const practiceAdminDocId = useMemo(() => {
    if (appName === 'providers-app') {
      return selectedPracticeId && id === selectedPracticeId ? selectedPracticeId : null
    }
    return adminRoles.includes(role || 'user') ? id : null
  }, [appName, selectedPracticeId, id, role])
  const { data: adminData } = useDocument<AdminPracticeData>(MIDWIVES_ADMIN, practiceAdminDocId)
  const { data: publicData } = useDocument<Practice>(MIDWIVES, id)
  const mergedData = useMemo<PracticeWithAdmin | null>(
    () => (publicData ? getPracticeWithAdmin(publicData, adminData ?? undefined) : null),
    [adminData, publicData],
  )
  const { fname, lname } = mergedData ?? {}
  const [viewLegacy, setViewLegacy] = useState(false)
  const [reportsOpen, setReportsOpen] = useState(false)

  const contextData = useMemo(() => ({ practice: mergedData, id }), [mergedData, id])
  return (
    <PracticeContext.Provider value={contextData}>
      <VStack align="flex-start" spacing={0} w="100%">
        <Flex pt={2} bg={colors.pink.hex} w="100%" pb={1} borderBottom="1px solid #cdcdcd">
          <VStack pr={10} w="100%" align="flex-start" spacing={0}>
            <HStack px={2} align="center" w="100%">
              <Text
                pl={1}
                fontWeight={600}
                textShadow="0 0 6px white"
                color="#222"
                fontFamily="Comfortaa"
                lineHeight={1}
                flex={1}
                fontSize="lg">
                {`${fname} ${lname}`}
              </Text>
              <ProviderAlertPopover midwife={provider} />
              {id ? (
                <Button
                  onClick={() => setReportsOpen(true)}
                  variant="outline"
                  bg="gray.50"
                  colorScheme="blue"
                  size="xs">
                  Reports
                </Button>
              ) : null}
              <Button
                onClick={() => setViewLegacy(!viewLegacy)}
                variant="outline"
                bg="gray.50"
                colorScheme={viewLegacy ? 'green' : 'gray'}
                size="xs">
                <HStack>
                  <ViewIcon height={4} />
                  <Text>{viewLegacy ? 'View New' : 'View Legacy'}</Text>
                </HStack>
              </Button>
            </HStack>
          </VStack>
        </Flex>
        <Flex w="100%" bg="whiteAlpha.600" borderRadius={4}>
          {viewLegacy ? <LegacyMidwifeView /> : <PracticeDataView />}
        </Flex>
        {/* <AdminMidwifeInfo /> */}
      </VStack>
      {id ? (
        <StageReportModal
          practiceId={id}
          isOpen={reportsOpen}
          onClose={() => setReportsOpen(false)}
        />
      ) : null}
    </PracticeContext.Provider>
  )
}

export const PatientMidwifeInfo: React.FC = () => {
  const { processResponse } = useContext(PopUpMessageContext)
  const {
    user,
    selectedAssessment: { ref, populated: selectedAssessment },
  } = useProfile()
  const { midwife } = selectedAssessment ?? {}
  const update = useUpdateDoc('practice')
  return (
    <Box width="100%">
      {midwife ? (
        <MidwifeInfo id={midwife.id} />
      ) : (
        <VStack p={2} spacing={0} w="100%">
          <Text fontSize="lg" color="gray.600" mb={2}>
            To edit midwife information, assign a midwife to {user?.name}
          </Text>
          <SimpleForm
            field={midwifeIdField}
            value={{ value: '' }}
            theme="detailed"
            boxProps={{ boxShadow: 'none', p: 0 }}
            onSubmit={async value => {
              if (!user || !selectedAssessment) {
                return processResponse({
                  error: 'Select or create an assessment first',
                })
              }
              return update(ref, '', {
                corrections: {
                  delivery: {
                    midwifeId: value,
                  },
                },
                midwifeId: value,
              })
            }}
          />
        </VStack>
      )}
    </Box>
  )
}
